import React, { useState } from 'react'
import { TreeExpandToggle } from '../Shared/TreeExpandToggle'
import { getChildAttributeIds } from '../../Utility/TreeUtility'
import {
  TREE_LEVEL_INDENT_PX,
  TreeCheckboxCell,
  TreeNodeInfo,
  TreeRadioCell,
  useSyncChildrenVisibilityForSearch,
} from './attributeTreeShared'

/**
 * Shared collapsible tree for Download attribute pickers (checkbox multi vs radio single).
 * Keeps one implementation so Sonar does not count duplicate blocks across CollapsibleCheckbox / CollapsibleRadio.
 */
const CollapsibleAttributeTree = ({
  mode,
  level = 0,
  className,
  checked,
  item,
  showItem,
  checkBoxClicked,
  showDefinition,
  selection,
  isSelected,
  searchTerm,
  isPlaceholder,
}) => {
  const [expandedItems, setExpandedItems] = useState(new Map())
  const itemIsPlaceholder = isPlaceholder ? isPlaceholder(item) : false

  const selectedCount =
    mode === 'checkbox' && item.Children?.length
      ? (selection?.Attributes || []).filter((id) => getChildAttributeIds(item).includes(id)).length
      : 0

  const toggleExpanded = () => {
    const itemId = item.AttributeID
    setExpandedItems((prev) => {
      const next = new Map(prev)
      const current = next.has(itemId)
        ? next.get(itemId)
        : (item.IsExpanded ?? false)
      next.set(itemId, !current)
      return next
    })
  }

  const isExpanded = expandedItems.has(item.AttributeID)
    ? expandedItems.get(item.AttributeID)
    : (item.IsExpanded ?? false)

  const handleParentInfoClick = () => {
    if (itemIsPlaceholder) {
      toggleExpanded()
    } else {
      checkBoxClicked(item)
    }
  }

  const parentKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleParentInfoClick()
    }
  }

  useSyncChildrenVisibilityForSearch(searchTerm)

  const nameSuffix =
    mode === 'checkbox' && item.Children?.length > 0 && !itemIsPlaceholder ? (
      <span className="tree-node-count">({selectedCount} selected)</span>
    ) : null

  const ControlCell = mode === 'checkbox' ? TreeCheckboxCell : TreeRadioCell

  const parentRowStyle = { paddingLeft: level * TREE_LEVEL_INDENT_PX }
  const leafRowStyle = { paddingLeft: (level + 1) * TREE_LEVEL_INDENT_PX }

  const renderLeafRow = (cItem) => (
    <div className="flex" key={cItem.AttributeID} style={leafRowStyle}>
      <div className="tree-gutter" aria-hidden="true" />
      <TreeNodeInfo
        tabIndex={isPlaceholder?.(cItem) ? -1 : 0}
        onKeyDown={isPlaceholder?.(cItem) ? undefined : (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            checkBoxClicked(cItem)
          }
        }}
        onClick={() => { !isPlaceholder?.(cItem) && checkBoxClicked(cItem) }}
        displayName={cItem.DisplayName}
        nameSuffix={null}
        showDefinition={showDefinition}
        comment={cItem.Comment}
      />
      {!(isPlaceholder && isPlaceholder(cItem)) && (
        <ControlCell className={className} checked={isSelected(cItem)} item={cItem} onItemClick={checkBoxClicked} />
      )}
    </div>
  )

  return (
    <div className='collapsiable-checkbox'>
      <div className='parent'>
        {showItem(item) && (
          <div className="flex" style={parentRowStyle}>
            <div className="tree-gutter">
              <TreeExpandToggle isExpanded={isExpanded} onToggle={toggleExpanded} />
            </div>
            <TreeNodeInfo
              tabIndex={itemIsPlaceholder ? -1 : 0}
              onKeyDown={parentKeyDown}
              onClick={handleParentInfoClick}
              displayName={item.DisplayName}
              nameSuffix={nameSuffix}
              showDefinition={showDefinition}
              comment={item.Comment}
            />
            {!itemIsPlaceholder && (
              <ControlCell className={className} checked={checked} item={item} onItemClick={checkBoxClicked} />
            )}
          </div>
        )}
      </div>
      <div className={`children ${!isExpanded && 'hideChildren'}`}>
        {item.Children.map((cItem) =>
          cItem.Children && cItem.Children.length ? (
            <CollapsibleAttributeTree
              key={cItem.AttributeID}
              mode={mode}
              level={level + 1}
              className={className}
              checked={isSelected(cItem)}
              item={cItem}
              showItem={showItem}
              checkBoxClicked={checkBoxClicked}
              showDefinition={showDefinition}
              selection={selection}
              isSelected={isSelected}
              searchTerm={searchTerm}
              isPlaceholder={isPlaceholder}
            />
          ) : (
            showItem(cItem) && renderLeafRow(cItem)
          )
        )}
      </div>
    </div>
  )
}

export default CollapsibleAttributeTree
